import React from "react";
import { useQuery } from "@apollo/client";
import {
  TenantBranch,
  TenantBranchesDocument,
} from "@quales/data-access/gql-server";
import { CustomTextField } from "@quales/form/textfield";
import { CustomAutoComplete } from "@quales/form/autocomplete";
import { useTranslations } from "use-intl";
import { Typography } from "@mui/material";

export interface BranchAutoCompleteProps {
  controlDisabled?: boolean;
  defaultValue?: number;
  label?: string;
  required?: boolean;
  width?: number | string;
  errors?: any;
  onValueChanged: (val: number) => void;
  disableClearable?: boolean;
}

export default function BranchAutoComplete({
  controlDisabled,
  defaultValue,
  required,
  label,
  errors,
  onValueChanged,
  disableClearable = false,
}: BranchAutoCompleteProps) {
  const [value, setValue] = React.useState<TenantBranch>(null);
  const [branches, setBranches] = React.useState<TenantBranch[]>([]);
  const t = useTranslations();
  const { data, loading } = useQuery(TenantBranchesDocument, {
    fetchPolicy: "network-only",
  });

  React.useEffect(() => {
    if (data && data.TenantBranches && data.TenantBranches.length > 0) {
      setBranches([
        {
          //@ts-ignore
          branchID: 0,
          branchType: t("value.ALL_BRANCHES"),
          branchName: t("value.ALL"),
        },
        ...data.TenantBranches,
      ]);
    } else {
      setBranches([]);
    }
  }, [data, loading]);

  React.useEffect(() => {
    if (defaultValue !== 0 && branches && branches.length > 0) {
      const foundRes = branches.find(
        (item: TenantBranch) =>
          item.branchID.toString() === defaultValue.toString()
      );

      if (foundRes) {
        //@ts-ignore
        setValue({
          ...foundRes,
        });
      }
    } else if (
      //@ts-ignore
      defaultValue === 0
    ) {
      setValue({
        //@ts-ignore
        branchID: 0,
        branchType: t("value.ALL_BRANCHES"),
        branchName: t("value.ALL"),
      });
    } else {
      setValue(undefined);
    }
  }, [defaultValue, branches]);

  const setInternalVal = (input: TenantBranch) => {
    setValue(input);
    if (input) {
      //@ts-ignore
      onValueChanged(input.branchID);
    }
  };

  return (
    <CustomAutoComplete
      id="BranchAutoComplete"
      disabled={controlDisabled}
      onChange={(val, newValue: TenantBranch) => setInternalVal(newValue)}
      isOptionEqualToValue={(option: TenantBranch, val: TenantBranch) =>
        option.branchID === val.branchID
      }
      getOptionLabel={(option: TenantBranch) => option.branchName}
      value={value || null}
      disableClearable={disableClearable}
      defaultValue={value || null}
      renderOption={(propsVal, option: TenantBranch) => (
        <li {...propsVal} key={option.branchID}>
          <Typography>{option.branchName}</Typography>
        </li>
      )}
      renderInput={(params) => (
        <CustomTextField
          {...params}
          size={"small"}
          required={required}
          controldisabled={controlDisabled}
          label={t("labels.branch")}
          placeholder={t("labels.branch")}
          error={!!errors}
          helperText={
            !!errors &&
            //@ts-ignore
            errors.message
          }
          InputProps={{
            ...params.InputProps,
          }}
        />
      )}
      options={branches}
    />
  );
}
